import axios from 'axios';

import env from 'config/env';
import { API_ENDPOINTS } from 'constants/apiEndpoints';
import { MESSAGES } from 'constants/messages';
import { ROUTES } from 'constants/routePaths';
import { STORAGE_KEYS } from 'constants/storageKeys';
import {
  decryptEnvelope,
  encryptEnvelope,
  isPayloadEncryptionAvailable,
} from 'util/payloadCrypto';
import { tokenManager } from 'util/tokenManager';

/**
 * Centralised Axios instance. Every service imports this — never create
 * ad-hoc axios calls in components or services.
 */
const httpClient = axios.create({
  baseURL: env.apiBaseUrl,
  timeout: env.apiTimeout,
  headers: { 'Content-Type': 'application/json' },
  // The refresh token lives in an httpOnly cookie (see auth.py _set_refresh_cookie,
  // scoped to path=/auth) so JS never touches it directly. Cookies ride along for
  // free on same-origin calls, but cross-origin (FE and API on different
  // hosts/ports, e.g. local dev) needs this explicit opt-in or the browser drops
  // them. CORS already pins allow_credentials to an explicit origin allowlist
  // (never "*"), so this is safe to enable unconditionally.
  withCredentials: true,
});

// ── Payload encryption (Layer C) ─────────────────────────────────────────────
// Mirrors app/core/payload_crypto.py. Encrypting the body is separate from
// announcing support: the X-Payload-Encrypted header goes on EVERY request
// (that is what makes the backend encrypt its response), while only JSON bodies
// are themselves encrypted. Multipart uploads are left alone — the backend
// skips non-JSON content types too, and buffering a 25 MB file to encrypt it
// would defeat streaming on both ends.
//
// Paths the backend keeps in plaintext (PAYLOAD_ENCRYPTION_EXEMPT_PATHS). The
// header is harmless on these, but skipping them avoids a pointless round of
// crypto on health polls.
const ENCRYPTION_EXEMPT = ['/api/health'];

const isExempt = (url = '') => ENCRYPTION_EXEMPT.some((path) => url.startsWith(path));

httpClient.interceptors.request.use(
  async (config) => {
    const token = tokenManager.getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // The X-Payload-Encrypted header, once stamped, is this guard: a request
    // replayed on the SAME config object (the silent-refresh retry below reuses
    // error.config) would otherwise pass back through here and get its
    // already-encrypted body encrypted a second time.
    if (
      isPayloadEncryptionAvailable()
      && !isExempt(config.url)
      && config.headers['X-Payload-Encrypted'] !== '1'
    ) {
      config.headers['X-Payload-Encrypted'] = '1';
      const hasJsonBody = config.data
        && !(config.data instanceof FormData)
        && !(config.data instanceof Blob);
      if (hasJsonBody) {
        config.data = await encryptEnvelope(config.data);
      }
    }
    return config;
  },
  (error) => Promise.reject(error),
);

/**
 * Decrypt a response body in place when the backend marked it encrypted.
 * Anything else (file downloads, exempt paths, a backend with the flag off)
 * passes through untouched, so this is safe to run on every response.
 */
const decryptResponseData = async (response) => {
  if (response?.headers?.['x-payload-encrypted'] !== '1') return response?.data;
  if (typeof response.data?.data !== 'string') return response?.data;
  return decryptEnvelope(response.data);
};

/** Map an Axios error to a normalised, user-friendly shape. */
const normaliseError = (error) => {
  if (!error.response) {
    return { status: 0, message: MESSAGES.ERRORS.NETWORK, data: null };
  }
  const { status, data } = error.response;
  const messageByStatus = {
    401: MESSAGES.AUTH.SESSION_EXPIRED,
    403: MESSAGES.ERRORS.FORBIDDEN,
    404: MESSAGES.ERRORS.NOT_FOUND,
    500: MESSAGES.ERRORS.SERVER,
  };
  const message = data?.message || messageByStatus[status] || MESSAGES.ERRORS.GENERIC;
  return { status, message, data };
};

// ── Silent access-token refresh on expiry ─────────────────────────────────────
// The access token is deliberately short-lived (60 min server-side — see
// ACCESS_TOKEN_EXPIRE_MINUTES in core/config.py); staying logged in depends on
// this refresh firing, not on that window being long. POST /auth/refresh reads
// the httpOnly cookie itself and hands back a new access token, so a single
// expired-token 401 is recoverable: refresh once, retry the original request,
// and the caller never sees the failure. Concurrent requests that expire
// together must not each trigger their own refresh (the backend would still
// honour every call since the session row isn't rotated per-refresh, but it's
// wasted round-trips) — so only the first one refreshes and the rest queue on
// its result.
let isRefreshing = false;
let refreshQueue = [];

const flushRefreshQueue = (error, token) => {
  refreshQueue.forEach(({ resolve, reject }) => (error ? reject(error) : resolve(token)));
  refreshQueue = [];
};

/** Resolves to a fresh access token, sharing one in-flight refresh across
 * however many requests expired at once. Rejects (and never mutates
 * tokenManager) if the refresh cookie is itself missing/expired/revoked. */
const refreshAccessToken = () => {
  if (isRefreshing) {
    return new Promise((resolve, reject) => refreshQueue.push({ resolve, reject }));
  }
  isRefreshing = true;
  // No body: POST /auth/refresh takes `body: RefreshRequest | None = None`, and
  // RefreshRequest.refresh_token is a REQUIRED field — sending `{}` would fail
  // that schema (422) instead of falling through to `body is None`, which is
  // what makes the endpoint read the httpOnly cookie instead.
  return httpClient
    .post(API_ENDPOINTS.AUTH.REFRESH)
    .then((result) => {
      const nextToken = result?.token;
      if (!nextToken) throw new Error('Refresh response carried no token.');
      tokenManager.setToken(nextToken);
      flushRefreshQueue(null, nextToken);
      return nextToken;
    })
    .catch((refreshError) => {
      flushRefreshQueue(refreshError, null);
      throw refreshError;
    })
    .finally(() => {
      isRefreshing = false;
    });
};

httpClient.interceptors.response.use(
  async (response) => {
    // Backend rotates the JWT via an X-New-Token response header — keep it fresh.
    const rotated = response.headers?.['x-new-token'];
    if (rotated) tokenManager.setToken(rotated);
    return decryptResponseData(response);
  },
  async (error) => {
    // Error bodies are encrypted too, and normaliseError reads data.message off
    // them — so decrypt before normalising or every failure would surface as
    // the generic fallback message instead of what the backend actually said.
    if (error.response?.headers?.['x-payload-encrypted'] === '1') {
      try {
        error.response.data = await decryptEnvelope(error.response.data);
      } catch {
        // Undecryptable error body: fall through and let normaliseError use its
        // status-based fallback rather than masking the original failure.
      }
    }
    const normalised = normaliseError(error);
    const url = error.config?.url || '';
    const isAuthRequest = url.includes('/auth/');
    const token = tokenManager.getToken();

    // ── When to auto-logout on 401 ──────────────────────────────────────────
    // Only treat a 401 as "session expired" when it comes from the identity
    // endpoint (GET /users/profile). All other 401s — e.g. a backend route
    // that denies access because of missing permissions, an admin endpoint not
    // yet available for the user's role, or a temporarily unavailable route —
    // must NOT force a logout. Instead they bubble up to the component as a
    // rejected promise so it can show an empty/error state gracefully.
    //
    // A local demo session (token === 'local-session') is never redirected
    // because the API simply 401s every call and components fall back to
    // their empty defaults.
    const isIdentityEndpoint = url.includes('/users/profile') || url.includes('/auth/me');

    // A genuinely expired/invalid/revoked JWT is a session-over signal from ANY
    // endpoint (the backend says "Token has expired." / "Invalid token." /
    // "Session has been revoked. Please log in again." — see
    // core/dependencies/auth.py), so clear + redirect rather than leaving the
    // user stuck seeing the error on every action (e.g. booking a seat). This is
    // distinct from a permission/role 401, which carries a different message.
    // "Revoked" covers token_version bumps — password change, email change,
    // account deactivation, or an explicit logout-everywhere — from another
    // session; the refresh attempt below will fail too (the refresh token's
    // embedded tv is stale as well) and fall straight through to the logout
    // branch, same as an outright expired token.
    const isSessionExpired = /token (has )?expired|invalid token|token is invalid|session has been revoked/i.test(
      normalised.message || '',
    );

    // A tenant-lifecycle 401 (app/core/tenant_status.py) — the org's subscription
    // ended or hasn't started, or its center was suspended/rejected — while a
    // token that is otherwise still perfectly valid keeps sending requests.
    // These carry a machine-readable `errors.code` (see assert_context_active),
    // unlike a permission/role 401, and mean the session is over right now, not
    // "try again after a refresh": Master editing the subscription dates (or
    // suspending a center) must take effect on an already-open session exactly
    // like `tenant_status`'s own module docstring promises, on ANY endpoint,
    // not only the identity one. Refreshing the access token would not help —
    // the new token mints through the very same check and fails identically.
    const tenantBlockCode = normalised.data?.errors?.code || '';
    const isTenantBlocked = /^subscription_|^center_/.test(tenantBlockCode);

    // A plain "expired" 401 (as opposed to invalid/revoked) is recoverable —
    // refresh once and silently replay the original request. error.config is
    // the failed request's own config, so re-invoking httpClient with it
    // re-issues the exact same call; _retried guards against looping if the
    // replay somehow 401s again.
    if (
      normalised.status === 401 &&
      isSessionExpired &&
      !isAuthRequest &&
      token &&
      token !== 'local-session' &&
      error.config &&
      !error.config._retried
    ) {
      error.config._retried = true;
      try {
        const newToken = await refreshAccessToken();
        error.config.headers = { ...error.config.headers, Authorization: `Bearer ${newToken}` };
        return httpClient(error.config);
      } catch {
        // Refresh cookie missing/expired/revoked too — fall through to the
        // logout branch below exactly as if this retry had never happened.
      }
    }

    if (
      normalised.status === 401 &&
      !isAuthRequest &&
      token &&
      token !== 'local-session' &&
      (isIdentityEndpoint || isSessionExpired || isTenantBlocked)
    ) {
      tokenManager.clear();
      if (isTenantBlocked && normalised.message) {
        // Read once by useLogin on the very next page load — window.location
        // .assign below is a hard navigation, so no in-memory React state
        // survives to carry this message across it.
        try {
          sessionStorage.setItem(STORAGE_KEYS.PENDING_AUTH_NOTICE, normalised.message);
        } catch {
          /* storage unavailable — the redirect still happens, just silently */
        }
      }
      if (window.location.pathname !== ROUTES.LOGIN) {
        window.location.assign(ROUTES.LOGIN);
      }
    }
    return Promise.reject(normalised);
  },
);

// ── Request de-duplication + short-lived GET cache ────────────────────────────
// Many screens mount several components that each fetch the same endpoint on the
// same tick (e.g. profile + permissions), and navigating back re-mounts refetch
// from scratch. De-duplicating concurrent identical GETs and briefly caching the
// result makes navigation feel instant and cuts redundant round-trips — while ANY
// write (POST/PUT/PATCH/DELETE) clears the cache so reads stay real-time/fresh.
const _get = httpClient.get.bind(httpClient);
const _inflight = new Map(); // key -> pending promise (shared by concurrent callers)
const _cache = new Map(); // key -> { at, value }
const GET_CACHE_TTL = 1500; // ms — long enough to collapse re-mount bursts, short enough to feel live

const _cacheKey = (url, config) => `${url}::${JSON.stringify(config?.params ?? {})}`;

const _clearGetCache = () => {
  _cache.clear();
  _inflight.clear();
};

httpClient.get = (url, config = {}) => {
  // Never cache binary/stream downloads (exports) or explicit opt-outs.
  if (config.cache === false || (config.responseType && config.responseType !== 'json')) {
    return _get(url, config);
  }
  const key = _cacheKey(url, config);
  const hit = _cache.get(key);
  if (hit && Date.now() - hit.at < GET_CACHE_TTL) {
    return Promise.resolve(hit.value);
  }
  if (_inflight.has(key)) return _inflight.get(key); // de-dupe concurrent identical GETs
  const pending = _get(url, config)
    .then((value) => {
      _cache.set(key, { at: Date.now(), value });
      return value;
    })
    .finally(() => {
      _inflight.delete(key);
    });
  _inflight.set(key, pending);
  return pending;
};

// A write invalidates the cache immediately and again once it settles, so the
// follow-up reload() after any submit/update reads fresh data.
['post', 'put', 'patch', 'delete'].forEach((method) => {
  const raw = httpClient[method].bind(httpClient);
  httpClient[method] = (...args) => {
    _clearGetCache();
    const result = raw(...args);
    Promise.resolve(result)
      .finally(_clearGetCache)
      .catch(() => {});
    return result;
  };
});

export default httpClient;
